import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import applicationService from '@/shared/services/applicationService';
import { nominationService } from '@/shared/services/nominationService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import { badgeTextFor } from '@/shared/utils/badgeText';
import {
    SelectedStudentsFields,
    findDuplicateLANominee,
    nomineeIdFieldFor,
} from './CreateApplicationSection5';
import StudentEmploymentRequirements from './StudentEmploymentRequirements';
import { requestedLACount } from './NomineeOfferPicker';

/**
 * The LA nominees on a submitted application, for its applicant and its
 * instructor of record: a faculty member who answered "I'm working on
 * selecting someone" hands over the names here, without reopening the rest
 * of the form (PUT /api/application/:id/nominees). The LA coordinator, or
 * an admin, changes them here on the faculty member's behalf, by the same
 * rules; the server emails the faculty member that they changed.
 *
 * Before LA decides the panel edits the application's nominee rows. Once
 * an LA award is visible to the faculty member, each nominee shows how
 * their nomination stands. A held decision is not visible, so until LA's
 * release moment the panel looks exactly as it does before a decision.
 */

// Statuses the server refuses, as the faculty member sees them
const CLOSED_STATUSES = [
    'draft',
    'revision_requested',
    'withdrawn',
    'rejected',
];

const AWARD_TYPES = ['approved', 'partial', 'contingent'];

// How a nomination stands, as the faculty member reads it. Same words as
// the reviewers' PositionNominees. Faculty never read a plain `released`:
// an accepted place that ended reaches them as unavailable, and an expired
// offer's ending carries endedAs, below.
const NOMINATION_BADGES = {
    offered: { bg: 'primary', label: 'Offered' },
    not_selected: { bg: 'secondary', label: 'Not offered' },
    accepted: { bg: 'success', label: 'Accepted' },
    declined: { bg: 'secondary', label: 'Declined' },
    expired: { bg: 'warning', label: 'Offer expired' },
    unavailable: { bg: 'dark', label: 'No longer available' },
};

// How an expired offer's place ended, by the server's endedAs marker
const ENDED_AS_BADGES = {
    released_to_applicants: { bg: 'dark', label: 'Released to applicants' },
    offered_to_another: {
        bg: 'dark',
        label: 'Place offered to another nominee',
    },
};

const badgeFor = (nomination) =>
    ENDED_AS_BADGES[nomination?.endedAs] ||
    NOMINATION_BADGES[nomination?.status] ||
    null;

// The nominations that hold one of the award's places
const HOLDING_STATUSES = ['offered', 'expired', 'accepted'];

const NINE_DIGITS = /^\d{9}$/;

// The server's words for a 900-number it refuses
const NOMINEE_ID_MESSAGE = "Enter the student's 900 number: nine digits.";

const idOf = (ref) => ref?._id ?? ref;

const emailKey = (value) =>
    typeof value === 'string' ? value.trim().toLowerCase() : '';

/**
 * Where LA stands, from the decisions the viewer can see: 'award' while an
 * award stands, 'closed' once LA denied the request or voided its
 * contingent award, otherwise 'open'.
 */
export function laStandingOf(application) {
    const decision = (application?.decisions || []).find(
        (d) => d.programType === 'la'
    );
    if (!decision) return 'open';
    if (decision.finalStatus === 'voided') return 'closed';
    if (AWARD_TYPES.includes(decision.type)) return 'award';
    return decision.type === 'waitlisted' ? 'open' : 'closed';
}

// The applicant or the instructor of record
const isRequestFaculty = (application, userId) =>
    idOf(application.applicant) === userId ||
    idOf(application.instructorOfRecord) === userId;

/**
 * Whether this viewer can change the application's LA nominees here: the
 * applicant, the instructor of record, the LA coordinator or an admin, on an
 * application that ranks LA, while the server would take the change.
 */
export function canEditLANominees(application, userId, roles = []) {
    if (!application || !userId) return false;
    const ranksLA = (application.programPreferences || []).some(
        (p) => p.programType === 'la'
    );
    const mayEdit =
        isRequestFaculty(application, userId) ||
        roles.includes('admin') ||
        roles.includes('la_coordinator');
    return (
        ranksLA &&
        mayEdit &&
        !CLOSED_STATUSES.includes(application.status) &&
        laStandingOf(application) !== 'closed'
    );
}

// The LA nominee rows the application shows. Mirrors the server's
// nomineeRowsOf (and nomineesOf in NomineeOfferPicker): with 'selecting'
// or 'needs_help' the form hid the rows, and an application saved before
// the question was asked has rows and no answer, which count
function nomineeRowsOf(application) {
    const status = application.programSpecificResponses?.la?.nomineeStatus;
    if (status !== undefined && status !== null && status !== 'selected') {
        return [];
    }
    return (application.namedStudents || [])
        .filter((s) => s.forProgram === 'la')
        .map(({ name, email, studentId }) => ({
            name: name || '',
            email: email || '',
            studentId: studentId || '',
        }));
}

// The 900-number fields the server would refuse, each with its message: a
// typed number that isn't nine digits. One already on the application is
// left alone, as the server does, since it may predate the rule.
function nomineeIdErrorsOf(rows, storedIds) {
    const errors = new Map();
    rows.forEach((row, position) => {
        const typed = row.studentId.trim();
        if (typed && !NINE_DIGITS.test(typed) && !storedIds.includes(typed)) {
            errors.set(nomineeIdFieldFor(position), NOMINEE_ID_MESSAGE);
        }
    });
    return errors;
}

// The ids of the fields the save would be refused for, in the order the
// rows show them. Same ids SelectedStudentsFields renders.
function nomineeErrorIds(rows, requestedCount, idErrors) {
    const ids = [];
    rows.forEach((row, position) => {
        for (const field of ['name', 'email', 'studentId']) {
            const id = `ca-la-nominee-${position}-${field}`;
            if (!row[field].trim() || idErrors.has(id)) ids.push(id);
        }
    });
    if (findDuplicateLANominee(rows)) {
        ids.push('ca-la-nominee-duplicate-error');
    }
    if (rows.length > requestedCount) ids.push('ca-la-nominee-count-error');
    return ids;
}

const trimmedRow = (row) => ({
    name: row.name.trim(),
    email: row.email.trim(),
    studentId: row.studentId.trim(),
});

// Whether the rows say what the application already says, ignoring
// surrounding spaces and the case of an address
const sameRows = (a, b) =>
    a.length === b.length &&
    a.every(
        (row, i) =>
            row.name.trim() === b[i].name.trim() &&
            emailKey(row.email) === emailKey(b[i].email) &&
            row.studentId.trim() === b[i].studentId.trim()
    );

const EMPTY_ROW = { name: '', email: '', studentId: '' };

function NomineesPanel({ application, onSaved, coordinatorReview = false }) {
    const { userProfile, effectiveRoles } = useAuth();
    // Changing them for the faculty member is part of coordinator review
    const roles = coordinatorReview
        ? resolveRoles({ effectiveRoles, userProfile })
        : [];
    const editable = canEditLANominees(application, userProfile?._id, roles);
    // Changing them for the faculty member, who is emailed about it
    const forFaculty =
        editable && !isRequestFaculty(application, userProfile?._id);
    const awarded = laStandingOf(application) === 'award';
    const requestedCount = requestedLACount(application);

    const [editing, setEditing] = useState(false);
    const [rows, setRows] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    // What the last save said: a confirmation, and why any nominee added
    // was not offered
    const [saved, setSaved] = useState(null);
    // The award's nominations, once loaded: null before then, or with no
    // award visible
    const [nominations, setNominations] = useState(null);
    const [nominationsFailed, setNominationsFailed] = useState(false);

    const saveErrorRef = useRevealAlert(saveError);
    const savedRef = useRevealAlert(saved);

    // The button that opens the editor unmounts while it's open, so focus
    // is placed when the panel switches: on the first field when it opens,
    // back on the button when it closes without saving. A save moves focus
    // to its alert instead.
    const toggleRef = useRef(null);
    const focusAfterSwitch = useRef(null);
    useEffect(() => {
        const target = focusAfterSwitch.current;
        focusAfterSwitch.current = null;
        if (target === 'editor') {
            document.getElementById('ca-la-nominee-0-name')?.focus();
        } else if (target === 'toggle') {
            toggleRef.current?.focus();
        }
    }, [editing]);

    const applicationId = application?._id;
    useEffect(() => {
        if (!editable || !awarded) {
            setNominations(null);
            return undefined;
        }
        let cancelled = false;
        setNominationsFailed(false);
        nominationService
            .getForApplication(applicationId)
            .then((data) => {
                if (!cancelled) setNominations(data.nominations);
            })
            .catch(() => {
                if (!cancelled) setNominationsFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [applicationId, editable, awarded]);

    if (!editable) return null;

    const current = nomineeRowsOf(application);
    const idErrors = nomineeIdErrorsOf(
        rows,
        current.map((row) => row.studentId)
    );
    const errorIds = nomineeErrorIds(rows, requestedCount, idErrors);
    const hasError = (id) => showErrors && errorIds.includes(id);

    const nominationFor = (row) =>
        (nominations || []).find(
            (n) =>
                n.programType === 'la' &&
                n.status !== 'voided' &&
                emailKey(n.email) === emailKey(row.email)
        );

    // Whether the nominees hold every place the award gives, so the note can
    // say that is why someone isn't offered. Otherwise a place may be open,
    // and the note claims nothing about it: whether the next nominee gets it
    // is the program's offer setting, which the save's notice reports.
    const laDecision = (application.decisions || []).find(
        (d) => d.programType === 'la'
    );
    const placesAwarded = laDecision?.awardedCount ?? requestedCount;
    const placesHeld = (nominations || []).filter(
        (n) => n.programType === 'la' && HOLDING_STATUSES.includes(n.status)
    ).length;
    const nomineesHoldEveryPlace = placesHeld >= placesAwarded;

    const startEditing = () => {
        setRows(current.length > 0 ? current : [{ ...EMPTY_ROW }]);
        setShowErrors(false);
        setSaveError(null);
        setSaved(null);
        focusAfterSwitch.current = 'editor';
        setEditing(true);
    };

    const closeEditor = () => {
        focusAfterSwitch.current = 'toggle';
        setEditing(false);
    };

    const cancelEditing = () => {
        closeEditor();
        setSaveError(null);
    };

    const handleSave = async (event) => {
        event.preventDefault();
        setShowErrors(true);
        setSaveError(null);
        if (errorIds.length > 0) {
            document.getElementById(errorIds[0])?.focus();
            return;
        }
        // Nothing to send, and nothing to tell the coordinator
        if (sameRows(rows, current)) {
            closeEditor();
            return;
        }
        setSaving(true);
        try {
            const result = await applicationService.updateNominees(
                applicationId,
                rows.map(trimmedRow)
            );
            if (result.nominations) setNominations(result.nominations);
            // Only a change to the named rows reaches the coordinator; an
            // edit to an accepted nominee alone is kept off the record.
            setSaved({
                notice: result.notice,
                changed: result.changed !== false,
            });
            setEditing(false);
            onSaved(result.application);
        } catch (err) {
            setSaveError(
                err.response?.data?.message ||
                    (forFaculty
                        ? 'The nominees were not saved. Please try again.'
                        : 'Your nominees were not saved. Please try again.')
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
                <h2 className="h5 mb-3">LA nominees</h2>
                {saved && (
                    <Alert
                        ref={savedRef}
                        tabIndex={-1}
                        variant={saved.notice ? 'info' : 'success'}
                    >
                        {!saved.changed && 'Nothing was changed.'}
                        {saved.changed &&
                            (forFaculty
                                ? 'The LA nominees were saved, and the faculty member was emailed that they changed.'
                                : 'Your LA nominees were saved, and the LA coordinator was told.')}
                        {saved.notice && (
                            <span className="d-block mt-2">{saved.notice}</span>
                        )}
                    </Alert>
                )}
                {editing ? (
                    <Form noValidate onSubmit={handleSave}>
                        <p className="small text-muted">
                            {forFaculty
                                ? `Name up to ${requestedCount} ${requestedCount === 1 ? 'student' : 'students'}, one for each LA requested. Only these names change, and the faculty member is emailed that they changed.`
                                : `Name up to ${requestedCount} ${requestedCount === 1 ? 'student' : 'students'}, one for each LA you requested. Only these names change; the rest of your request stays as submitted.`}
                        </p>
                        <StudentEmploymentRequirements headingId="np-la-requirements-heading" />
                        <SelectedStudentsFields
                            nominees={rows.map((student, index) => ({
                                student,
                                index,
                            }))}
                            requestedCount={requestedCount}
                            onAdd={() => setRows([...rows, { ...EMPTY_ROW }])}
                            onUpdate={(index, field, value) =>
                                setRows(
                                    rows.map((row, i) =>
                                        i === index
                                            ? { ...row, [field]: value }
                                            : row
                                    )
                                )
                            }
                            onRemove={(index) =>
                                setRows(rows.filter((_, i) => i !== index))
                            }
                            hasError={hasError}
                            nomineeIdErrors={idErrors}
                            allowRemovingLast
                            canRaiseCount={false}
                        />
                        {saveError && (
                            <Alert
                                ref={saveErrorRef}
                                tabIndex={-1}
                                variant="danger"
                            >
                                {saveError}
                            </Alert>
                        )}
                        <div className="d-flex flex-wrap gap-2">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={saving}
                            >
                                {saving ? 'Saving' : 'Save nominees'}
                            </Button>
                            <Button
                                variant="outline-primary"
                                onClick={cancelEditing}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                ) : (
                    <>
                        {current.length === 0 ? (
                            <p className="mb-3">
                                {forFaculty
                                    ? 'No LA nominee is named on this request. You can add one for the faculty member here.'
                                    : "You haven't named an LA nominee. You can add one here without reopening your request."}
                            </p>
                        ) : (
                            <ul className="list-unstyled mb-3">
                                {current.map((row) => {
                                    const badge = badgeFor(nominationFor(row));
                                    return (
                                        <li key={row.email} className="mb-2">
                                            <span className="fw-semibold">
                                                {row.name}
                                            </span>{' '}
                                            <span className="text-muted">
                                                {row.email}
                                            </span>
                                            {badge && (
                                                <Badge
                                                    bg={badge.bg}
                                                    text={badgeTextFor(
                                                        badge.bg
                                                    )}
                                                    className="ms-2"
                                                >
                                                    {badge.label}
                                                </Badge>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {current.some(
                            (row) =>
                                nominationFor(row)?.status === 'not_selected'
                        ) && (
                            <p className="small text-muted">
                                {nomineesHoldEveryPlace
                                    ? `Not offered means every place the LA program awarded is already offered to another ${forFaculty ? 'nominee' : 'of your nominees'}.`
                                    : "Not offered means the LA program hasn't offered that student a place."}
                            </p>
                        )}
                        {awarded && nominationsFailed && (
                            <p className="small text-muted">
                                Where each nomination stands couldn&apos;t be
                                loaded. Reload the page to try again.
                            </p>
                        )}
                        <Button
                            ref={toggleRef}
                            variant="outline-primary"
                            size="sm"
                            onClick={startEditing}
                        >
                            {current.length === 0
                                ? 'Add a nominee'
                                : 'Change nominees'}
                        </Button>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}

NomineesPanel.propTypes = {
    application: PropTypes.shape({
        _id: PropTypes.string,
        status: PropTypes.string,
        applicant: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        instructorOfRecord: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object,
        ]),
        programPreferences: PropTypes.array,
        namedStudents: PropTypes.array,
        programSpecificResponses: PropTypes.object,
        decisions: PropTypes.array,
    }).isRequired,
    onSaved: PropTypes.func.isRequired,
    // Whether coordinator review is on, so the LA coordinator can edit
    coordinatorReview: PropTypes.bool,
};

export default NomineesPanel;
